import { useCallback, useEffect, useRef, useState } from "react";
import { INDIA_CUSTOMER_MAP_SVG } from "../data/indiaCustomerMapSvg";
import { CUSTOMERS, KOLKATA_PRESENCE_COMPANIES, TAG_CLASS } from "../data/indiaCustomerMapCustomers";
import PresenceGlobeMap from "./PresenceGlobeMap";
import {
  NCR_CHAIN,
  PRESENCE_BY_KEY,
  PRESENCE_LOCATIONS,
  OFFICE_TYPE,
} from "../data/presenceMapLocations";

const POPUP_W = 215;
const POPUP_H = 158;
const POPUP_LIST_W = 280;
const POPUP_LIST_H = 320;
const PRESENCE_POPUP_W = 200;
const PRESENCE_POPUP_H = 90;
const LOCATION_SCROLL_PX_PER_SEC = 28;
const LOCATION_SCROLL_PAUSE_MS = 2000;
const SVG_NS = "http://www.w3.org/2000/svg";

function startLocationListAutoScroll(listEl, cancelRef) {
  const maxScroll = listEl.scrollHeight - listEl.clientHeight;
  if (maxScroll <= 0) return undefined;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    listEl.scrollTop = 0;
    return undefined;
  }

  let paused = false;
  let pauseUntil = 0;
  let lastTime = performance.now();

  listEl.scrollTop = 0;

  const tick = (now) => {
    if (paused) {
      if (now >= pauseUntil) {
        paused = false;
        listEl.scrollTop = 0;
        lastTime = now;
      }
      cancelRef.current = requestAnimationFrame(tick);
      return;
    }

    const deltaSec = Math.min((now - lastTime) / 1000, 0.05);
    lastTime = now;
    const next = listEl.scrollTop + LOCATION_SCROLL_PX_PER_SEC * deltaSec;

    if (next >= maxScroll) {
      listEl.scrollTop = maxScroll;
      paused = true;
      pauseUntil = now + LOCATION_SCROLL_PAUSE_MS;
    } else {
      listEl.scrollTop = next;
    }

    cancelRef.current = requestAnimationFrame(tick);
  };

  cancelRef.current = requestAnimationFrame(tick);

  return () => {
    if (cancelRef.current) {
      cancelAnimationFrame(cancelRef.current);
      cancelRef.current = null;
    }
  };
}

function getMapPins(svg) {
  return Array.from(svg.querySelectorAll(".city-group"))
    .map((group) => {
      const core = group.querySelector(".city-core");
      if (!core) return null;
      return {
        key: group.dataset.city,
        x: parseFloat(core.getAttribute("cx")),
        y: parseFloat(core.getAttribute("cy")),
      };
    })
    .filter(Boolean);
}

function getHubPin(pins) {
  return pins.find((p) => p.key === "delhi") ?? {
    x: pins.reduce((sum, p) => sum + p.x, 0) / pins.length,
    y: pins.reduce((sum, p) => sum + p.y, 0) / pins.length,
  };
}

function sampleQuadratic(a, control, b, steps = 16) {
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const mt = 1 - t;
    points.push({
      x: mt * mt * a.x + 2 * mt * t * control.x + t * t * b.x,
      y: mt * mt * a.y + 2 * mt * t * control.y + t * t * b.y,
    });
  }
  return points;
}

function segmentsIntersect(p1, p2, p3, p4) {
  const cross = (ax, ay, bx, by, cx, cy) => (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const onSegment = (ax, ay, bx, by, cx, cy) =>
    Math.min(ax, bx) <= cx + 1e-6 &&
    cx <= Math.max(ax, bx) + 1e-6 &&
    Math.min(ay, by) <= cy + 1e-6 &&
    cy <= Math.max(ay, by) + 1e-6;

  const d1 = cross(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);
  const d2 = cross(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y);
  const d3 = cross(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y);
  const d4 = cross(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  if (Math.abs(d1) < 1e-6 && onSegment(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)) return true;
  if (Math.abs(d2) < 1e-6 && onSegment(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y)) return true;
  if (Math.abs(d3) < 1e-6 && onSegment(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y)) return true;
  if (Math.abs(d4) < 1e-6 && onSegment(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y)) return true;

  return false;
}

function curvesIntersect(samplesA, samplesB) {
  for (let i = 1; i < samplesA.length; i += 1) {
    for (let j = 1; j < samplesB.length; j += 1) {
      const a1 = samplesA[i - 1];
      const a2 = samplesA[i];
      const b1 = samplesB[j - 1];
      const b2 = samplesB[j];
      const sharesEndpoint =
        (Math.hypot(a1.x - b1.x, a1.y - b1.y) < 1.5) ||
        (Math.hypot(a1.x - b2.x, a1.y - b2.y) < 1.5) ||
        (Math.hypot(a2.x - b1.x, a2.y - b1.y) < 1.5) ||
        (Math.hypot(a2.x - b2.x, a2.y - b2.y) < 1.5);
      if (sharesEndpoint) continue;
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

function buildConnectionEdges(pins) {
  const hub = getHubPin(pins);
  const hubKey = hub.key ?? "delhi";
  const ncrSet = new Set(NCR_CHAIN);
  const targets = pins
    .filter((p) => p.key !== hubKey && !ncrSet.has(p.key))
    .sort(
      (a, b) =>
        Math.atan2(a.y - hub.y, a.x - hub.x) - Math.atan2(b.y - hub.y, b.x - hub.x),
    );

  const edges = [];
  const placed = [];

  targets.forEach((target, index) => {
    const dx = target.x - hub.x;
    const dy = target.y - hub.y;
    const len = Math.hypot(dx, dy) || 1;
    const mx = (hub.x + target.x) / 2;
    const my = (hub.y + target.y) / 2;
    const px = -dy / len;
    const py = dx / len;
    const baseOffset = Math.min(42, Math.max(14, len * 0.1));
    const signOptions = index % 2 === 0 ? [1, -1] : [-1, 1];

    let chosen = null;
    signOptions.some((sign) => {
      const offset = baseOffset + (index % 4) * 4;
      const control = {
        cx: mx + px * offset * sign,
        cy: my + py * offset * sign,
      };
      const samples = sampleQuadratic(hub, { x: control.cx, y: control.cy }, target);
      const overlaps = placed.some((prev) => curvesIntersect(samples, prev.samples));
      if (!overlaps) {
        chosen = { target, control, samples };
        return true;
      }
      return false;
    });

    if (!chosen) {
      chosen = {
        target,
        control: { cx: mx, cy: my },
        samples: sampleQuadratic(hub, { x: mx, y: my }, target),
      };
    }

    edges.push(chosen);
    placed.push(chosen);
  });

  return edges;
}

function svgEl(tag, attrs) {
  const el = document.createElementNS(SVG_NS, tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, String(v)));
  return el;
}

function createPresencePin(loc) {
  const isOffice = loc.type === OFFICE_TYPE.HEAD || loc.type === OFFICE_TYPE.BRANCH;
  const isGlobal = loc.type === OFFICE_TYPE.GLOBAL;
  const { x, y } = loc;

  const outerR = isOffice ? 17 : isGlobal ? 12 : 13;
  const ringR = isOffice ? 10 : isGlobal ? 7.5 : 7.5;
  const coreR = isOffice ? 4.5 : isGlobal ? 3.2 : 3.2;
  const pinR = isOffice ? 5.5 : isGlobal ? 4 : 4;
  const stemTop = y - (isOffice ? 10 : 8);
  const pinY = stemTop - 5;

  const g = svgEl("g", {
    class: `city-group office-${loc.type}`,
    "data-city": loc.key,
  });

  if (isOffice) {
    g.appendChild(svgEl("circle", { class: "pulse-ring", cx: x, cy: y, r: 7 }));
    g.appendChild(svgEl("circle", { class: "pulse-ring d2", cx: x, cy: y, r: 7 }));
  }

  g.appendChild(svgEl("circle", { class: "city-outer", cx: x, cy: y, r: outerR }));
  g.appendChild(svgEl("circle", { class: "city-ring", cx: x, cy: y, r: ringR }));
  g.appendChild(svgEl("circle", { class: "city-core", cx: x, cy: y, r: coreR }));
  g.appendChild(svgEl("line", {
    class: "pin-stem",
    x1: x,
    y1: y - (isOffice ? 10 : 8),
    x2: x,
    y2: pinY + pinR,
  }));
  g.appendChild(svgEl("circle", { class: "pin-head", cx: x, cy: pinY, r: pinR }));

  return g;
}

function createPresenceLabel(loc) {
  const text = svgEl("text", {
    x: loc.x + 6,
    y: loc.y - 14,
    "font-family": "Space Mono, monospace",
    fill: "rgba(200,220,240,0.55)",
    "font-size": loc.type === OFFICE_TYPE.GLOBAL ? 8 : 9,
  });
  text.textContent = loc.city;
  return text;
}

function ensureConnectionStyles() {
  const styleId = "india-map-connections-style";
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.textContent = `
      .conn-path { stroke: rgba(247,152,57,0.95); stroke-width: 2; fill: none; stroke-dasharray: 6 6; opacity: 0.9; }
      .conn-path.ncr-chain { stroke-width: 2.2; opacity: 1; }
      @keyframes conn-dash { to { stroke-dashoffset: -24; } }
      .conn-path.animate { animation: conn-dash 2s linear infinite; }
    `;
    document.head.appendChild(styleEl);
  }
  return styleEl;
}

export default function IndiaCustomerMapSection({ compactStats = false, presenceMode = false }) {
  const mapCardRef = useRef(null);
  const locationsListRef = useRef(null);
  const hideTimerRef = useRef(null);
  const scrollAnimRef = useRef(null);
  const [activeKey, setActiveKey] = useState(null);
  const [popup, setPopup] = useState({ show: false, left: 0, top: 0, flipUp: false });
  const [presenceReady, setPresenceReady] = useState(false);
  const kolkataHubOnly = compactStats && !presenceMode;

  const hidePopup = useCallback(() => {
    setPopup((p) => ({ ...p, show: false }));
    setActiveKey(null);
  }, []);

  const cancelHidePopup = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHidePopup = useCallback(() => {
    cancelHidePopup();
    hideTimerRef.current = setTimeout(hidePopup, 160);
  }, [cancelHidePopup, hidePopup]);

  const showPopup = useCallback((el, key) => {
    if (kolkataHubOnly && key !== "kolkata") return;

    const mapCard = mapCardRef.current;
    if (!mapCard || !el) return;
    if (presenceMode && !PRESENCE_BY_KEY[key]) return;
    if (!presenceMode && !kolkataHubOnly && !CUSTOMERS[key]) return;

    cancelHidePopup();

    const popupW = presenceMode
      ? PRESENCE_POPUP_W
      : kolkataHubOnly
        ? POPUP_LIST_W
        : POPUP_W;
    const popupH = presenceMode
      ? PRESENCE_POPUP_H
      : kolkataHubOnly
        ? POPUP_LIST_H
        : POPUP_H;
    const wR = mapCard.getBoundingClientRect();
    const eR = el.getBoundingClientRect();
    const cx = eR.left + eR.width / 2 - wR.left;
    const ty = eR.top - wR.top;
    const by = eR.bottom - wR.top;

    if (kolkataHubOnly) {
      let left = cx - popupW - 18;
      if (left < 8) left = Math.max(8, cx - popupW / 2);
      if (left + popupW > mapCard.offsetWidth - 8) {
        left = mapCard.offsetWidth - popupW - 8;
      }

      let top = Math.max(8, ty - 24);
      const maxTop = mapCard.offsetHeight - popupH - 8;
      if (top > maxTop) top = Math.max(8, maxTop);

      setActiveKey(key);
      setPopup({ show: true, left, top, flipUp: false });
      return;
    }

    let left = cx - 20;
    if (left + popupW > mapCard.offsetWidth - 8) left = mapCard.offsetWidth - popupW - 8;
    if (left < 8) left = 8;

    const flipUp = by + popupH + 14 > mapCard.offsetHeight;

    setActiveKey(key);
    setPopup({
      show: true,
      left,
      top: flipUp ? ty - popupH - 12 : by + 10,
      flipUp,
    });
  }, [kolkataHubOnly, presenceMode, cancelHidePopup]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
  }, []);

  useEffect(() => {
    if (!kolkataHubOnly || !popup.show || activeKey !== "kolkata") {
      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
      return undefined;
    }

    let stopScroll = null;
    const startTimer = setTimeout(() => {
      const listEl = locationsListRef.current;
      if (!listEl) return;
      stopScroll = startLocationListAutoScroll(listEl, scrollAnimRef);
    }, 220);

    return () => {
      clearTimeout(startTimer);
      if (stopScroll) stopScroll();
      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
    };
  }, [kolkataHubOnly, popup.show, activeKey]);

  useEffect(() => {
    if (presenceMode && !presenceReady) return undefined;

    const mapCard = mapCardRef.current;
    if (!mapCard) return undefined;

    const groups = mapCard.querySelectorAll(".city-group");
    const cleanups = [];

    groups.forEach((g) => {
      if (kolkataHubOnly && g.dataset.city !== "kolkata") return;
      const onEnter = () => showPopup(g, g.dataset.city);
      const onLeave = () => (kolkataHubOnly ? scheduleHidePopup() : hidePopup());
      g.addEventListener("mouseenter", onEnter);
      g.addEventListener("mouseleave", onLeave);
      cleanups.push(() => {
        g.removeEventListener("mouseenter", onEnter);
        g.removeEventListener("mouseleave", onLeave);
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [showPopup, hidePopup, scheduleHidePopup, kolkataHubOnly, presenceMode, presenceReady]);

  useEffect(() => {
    if (!kolkataHubOnly) return undefined;

    const mapCard = mapCardRef.current;
    const svg = mapCard?.querySelector("#india-svg");
    if (!svg) return undefined;

    svg.querySelectorAll(".city-group").forEach((group) => {
      if (group.dataset.city !== "kolkata") {
        group.style.display = "none";
      }
    });
    svg.querySelectorAll("text").forEach((label) => {
      if (label.textContent.trim() !== "Kolkata") {
        label.style.display = "none";
      }
    });

    return () => {
      svg.querySelectorAll(".city-group").forEach((group) => {
        group.style.display = "";
      });
      svg.querySelectorAll("text").forEach((label) => {
        label.style.display = "";
      });
    };
  }, [kolkataHubOnly]);

  useEffect(() => {
    if (!presenceMode) return undefined;

    const mapCard = mapCardRef.current;
    const svg = mapCard?.querySelector("#india-svg");
    if (!svg) return undefined;

    svg.querySelectorAll(".city-group").forEach((g) => g.remove());
    svg.querySelectorAll("text").forEach((t) => t.remove());

    const pinsLayer = svgEl("g", { class: "presence-pins" });
    const labelsLayer = svgEl("g", {
      class: "presence-labels",
      "font-family": "Space Mono, monospace",
      fill: "rgba(200,220,240,0.55)",
    });

    PRESENCE_LOCATIONS.forEach((loc) => {
      pinsLayer.appendChild(createPresencePin(loc));
      labelsLayer.appendChild(createPresenceLabel(loc));
    });

    svg.appendChild(pinsLayer);
    svg.appendChild(labelsLayer);

    const styleEl = ensureConnectionStyles();
    const connGroup = svgEl("g", { class: "connections" });
    svg.appendChild(connGroup);

    const pinMap = Object.fromEntries(
      PRESENCE_LOCATIONS.map((loc) => [loc.key, { key: loc.key, x: loc.x, y: loc.y }]),
    );

    for (let i = 0; i < NCR_CHAIN.length - 1; i += 1) {
      const from = pinMap[NCR_CHAIN[i]];
      const to = pinMap[NCR_CHAIN[i + 1]];
      if (!from || !to) continue;
      const path = svgEl("path", {
        d: `M ${from.x} ${from.y} L ${to.x} ${to.y}`,
        class: "conn-path ncr-chain animate",
        "vector-effect": "non-scaling-stroke",
      });
      connGroup.appendChild(path);
    }

    const hub = pinMap.delhi;
    const ncrSet = new Set(NCR_CHAIN);
    const remotePins = PRESENCE_LOCATIONS
      .filter((loc) => loc.key !== "delhi" && !ncrSet.has(loc.key))
      .map((loc) => ({ key: loc.key, x: loc.x, y: loc.y }));

    const edges = buildConnectionEdges([hub, ...remotePins]);
    edges.forEach(({ target, control }) => {
      const path = svgEl("path", {
        d: `M ${hub.x} ${hub.y} Q ${control.cx} ${control.cy} ${target.x} ${target.y}`,
        class: "conn-path animate",
        "vector-effect": "non-scaling-stroke",
      });
      connGroup.appendChild(path);
    });

    setPresenceReady(true);

    return () => {
      setPresenceReady(false);
      pinsLayer.remove();
      labelsLayer.remove();
      connGroup.remove();
      if (styleEl?.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, [presenceMode]);

  const active = activeKey
    ? presenceMode
      ? PRESENCE_BY_KEY[activeKey]
      : CUSTOMERS[activeKey]
    : null;

  useEffect(() => {
    if (presenceMode || kolkataHubOnly) return undefined;

    const mapCard = mapCardRef.current;
    if (!mapCard) return undefined;

    const svg = mapCard.querySelector("#india-svg");
    if (!svg) return undefined;

    const styleEl = ensureConnectionStyles();

    let connGroup = svg.querySelector("g.connections");
    if (!connGroup) {
      connGroup = document.createElementNS(SVG_NS, "g");
      connGroup.setAttribute("class", "connections");
      svg.appendChild(connGroup);
    }

    const pins = getMapPins(svg);
    const hub = getHubPin(pins);
    const edges = buildConnectionEdges(pins);

    const paths = [];
    edges.forEach(({ target, control }) => {
      const path = document.createElementNS(SVG_NS, "path");
      const d = `M ${hub.x} ${hub.y} Q ${control.cx} ${control.cy} ${target.x} ${target.y}`;
      path.setAttribute("d", d);
      path.setAttribute("class", "conn-path animate");
      path.setAttribute("vector-effect", "non-scaling-stroke");
      connGroup.appendChild(path);
      paths.push(path);
    });

    return () => {
      if (connGroup) connGroup.remove();
      if (styleEl?.parentNode) styleEl.parentNode.removeChild(styleEl);
      paths.length = 0;
    };
  }, [presenceMode, kolkataHubOnly]);

  const indiaPresenceCount = PRESENCE_LOCATIONS.filter(
    (loc) => loc.type !== OFFICE_TYPE.GLOBAL,
  ).length;

  return (
    <div className="section-wrapper india-customer-map-section">
      <div className="india-map-split">
        <div className="india-map-content">
          <div className="section-header">
            <span className="eyebrow">
              {presenceMode ? "// Our Footprint" : "// Customer Network"}
            </span>
            <h2>
              {presenceMode ? (
                <>
                  Offices &amp; Presence Across{" "}
                  <span style={{ color: "var(--accent)" }}>India &amp; Beyond</span>
                </>
              ) : (
                <>
                  Our Presence Across <span style={{ color: "var(--accent)" }}>India</span>
                </>
              )}
            </h2>
            <p>
              {presenceMode
                ? "Explore our global footprint — click India, UAE, or South Africa on the world map to view office & presence pins."
                : kolkataHubOnly
                  ? "Hover over Kolkata to see our customer companies auto-scroll."
                  : "Hover over any location pin to see customer details."}
            </p>
          </div>

          <div className={`stats-bar${compactStats || presenceMode ? " stats-bar--compact" : ""}`}>
            {presenceMode ? (
              <>
                <div className="stat-item"><span className="num">1</span><span className="lbl">Head Office</span></div>
                <div className="stat-item"><span className="num">2</span><span className="lbl">Branch Offices</span></div>
                <div className="stat-item"><span className="num">{indiaPresenceCount}</span><span className="lbl">India Presence</span></div>
                <div className="stat-item"><span className="num">2</span><span className="lbl">Global Presence</span></div>
              </>
            ) : (
              <>
                <div className="stat-item"><span className="num">400+</span><span className="lbl">Enterprise Clients</span></div>
                {!compactStats && (
                  <>
                    <div className="stat-item"><span className="num">15</span><span className="lbl">Cities</span></div>
                    <div className="stat-item"><span className="num">₹42Cr</span><span className="lbl">Revenue</span></div>
                    <div className="stat-item"><span className="num">96%</span><span className="lbl">Retention</span></div>
                  </>
                )}
              </>
            )}
          </div>

          {(presenceMode || !kolkataHubOnly) && (
            <div className="legend">
              {presenceMode ? (
                <>
                  <div className="leg-item"><div className="leg-dot ld-office" /> Head &amp; Branch Office</div>
                  <div className="leg-item"><div className="leg-dot ld-presence" /> Presence (India &amp; Global)</div>
                </>
              ) : (
                <>
                  <div className="leg-item"><div className="leg-dot ld-ent" /> Enterprise</div>
                  <div className="leg-item"><div className="leg-dot ld-smb" /> SMB</div>
                  <div className="leg-item"><div className="leg-dot ld-sta" /> Startup</div>
                  <div className="leg-item"><div className="leg-dot ld-par" /> Partner</div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="map-layout">
          {presenceMode ? (
            <div className="map-card map-card--globe">
              <PresenceGlobeMap />
            </div>
          ) : (
            <div className="map-card" id="mapCard" ref={mapCardRef}>
              <div
                className={`popup-wrap${popup.show ? " show" : ""}${kolkataHubOnly ? " locations-popup-wrap" : ""}`}
                id="popup"
                style={{ left: `${popup.left}px`, top: `${popup.top}px` }}
                onMouseEnter={kolkataHubOnly ? cancelHidePopup : undefined}
                onMouseLeave={kolkataHubOnly ? hidePopup : undefined}
              >
                {kolkataHubOnly && activeKey === "kolkata" && (
                  <div className={`popup-card india-map-locations-popup${popup.flipUp ? " arrow-up" : ""}`}>
                    <div className="pop-city">Kolkata</div>
                    <div className="pop-locations-sub">Our Customers</div>
                    <ul className="pop-locations-list" ref={locationsListRef}>
                      {KOLKATA_PRESENCE_COMPANIES.map((company) => (
                        <li key={company}>{company}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {!kolkataHubOnly && active && (
                  <div className={`popup-card${popup.flipUp ? " arrow-up" : ""}`} id="popupCard">
                    <div className="pop-city">{active.city}</div>
                    <div className="pop-name">{active.company}</div>
                    <div className="pop-tags">
                      {active.tags.map((t) => (
                        <span key={t} className={`ptag ${TAG_CLASS[t]}`}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="map-svg-host" dangerouslySetInnerHTML={{ __html: INDIA_CUSTOMER_MAP_SVG }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
